import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getAuthUrl } from "@/utils/redirectUtils";
import {
  DollarSign,
  Clock,
  Target,
  Search,
  Filter,
  Layers,
  BarChart3,
  ArrowRight,
  TrendingUp,
  Zap,
  X,
  Check,
  Eye,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Animation helpers                                                  */
/* ------------------------------------------------------------------ */

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const KeywordOptimisationFR = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Optimisation des Mots-Cl&eacute;s | Google Ads Budget-Intelligent | Upfixe</title>
        <meta
          name="description"
          content="Arr&ecirc;tez de gaspiller votre budget Google Ads sur des mots-cl&eacute;s trop chers. Upfixe filtre par CPA, priorise par volume de recherche et organise en groupes d'annonces th&eacute;matiques."
        />
        <meta
          name="keywords"
          content="optimisation mots-cl&eacute;s, strat&eacute;gie mots-cl&eacute;s google ads, s&eacute;lection mots-cl&eacute;s budget, optimisation budget google ads, groupes annonces th&eacute;matiques"
        />
        <link rel="canonical" href="https://upfixe.com/fr/optimisation-mots-cles" />
        <meta property="og:title" content="Optimisation des Mots-Cl&eacute;s | Google Ads Budget-Intelligent | Upfixe" />
        <meta
          property="og:description"
          content="Arr&ecirc;tez de gaspiller votre budget Google Ads. Pipeline intelligent de mots-cl&eacute;s adapt&eacute; &agrave; votre budget."
        />
      </Helmet>

      <Header />

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">

          {/* ───────────────── Hero ───────────────── */}
          <section className="text-center mb-24 max-w-4xl mx-auto">
            <motion.div {...fadeUp()}>
              <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/10 px-4 py-1 text-sm">
                Pipeline de mots-cl&eacute;s en 5 &eacute;tapes
              </Badge>
            </motion.div>

            <motion.h1
              {...fadeUp(0.1)}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              Arr&ecirc;tez de gaspiller votre budget sur des mots-cl&eacute;s{" "}
              <span className="text-primary">que vous ne pouvez pas vous permettre</span>
            </motion.h1>

            <motion.p
              {...fadeUp(0.2)}
              className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto"
            >
              Upfixe analyse votre budget, filtre les mots-cl&eacute;s trop chers, priorise par volume de recherche
              et cr&eacute;e des groupes d'annonces th&eacute;matiques &mdash; en moins de 5 minutes.
            </motion.p>

            <motion.div {...fadeUp(0.3)} className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="text-lg px-8 h-14 font-semibold"
                onClick={() => (window.location.href = getAuthUrl())}
              >
                Commencer gratuitement <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 h-14" asChild>
                <Link to="/how-it-works">D&eacute;couvrir le fonctionnement</Link>
              </Button>
            </motion.div>
          </section>

          {/* ───────────────── Problem ───────────────── */}
          <section className="mb-24">
            <motion.div {...fadeUp()} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                La plupart des annonceurs &eacute;puisent leur budget avant 10h
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Des mots-cl&eacute;s trop chers, z&eacute;ro visibilit&eacute;, et des annonces qui ne parlent pas &agrave; vos clients.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                {
                  icon: <DollarSign className="h-7 w-7" />,
                  title: "&Eacute;puisement du budget",
                  desc: "80 % du budget consomm&eacute; par 2-3 mots-cl&eacute;s ultra-comp&eacute;titifs. Le reste de vos opportunit&eacute;s est ignor&eacute;.",
                },
                {
                  icon: <Eye className="h-7 w-7" />,
                  title: "Z&eacute;ro visibilit&eacute;",
                  desc: "Apr&egrave;s 10h du matin, vos annonces disparaissent. Vos concurrents captent tout le trafic de l'apr&egrave;s-midi.",
                },
                {
                  icon: <Target className="h-7 w-7" />,
                  title: "Faible pertinence",
                  desc: "Des mots-cl&eacute;s g&eacute;n&eacute;riques dans un seul groupe d'annonces = Quality Score bas et CPC gonfl&eacute;.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp(i * 0.1)}
                  className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center"
                >
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* ───────────────── 5-Step Pipeline ───────────────── */}
          <section className="mb-24">
            <motion.div {...fadeUp()} className="text-center mb-14">
              <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/10 px-4 py-1 text-sm">
                Pipeline en 5 &eacute;tapes
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Comment Upfixe optimise vos mots-cl&eacute;s
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Un processus automatis&eacute; qui transforme votre budget en campagnes rentables.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto space-y-8">
              {[
                {
                  step: 1,
                  icon: <BarChart3 className="h-6 w-6" />,
                  title: "Analyse Budget & CPA",
                  desc: "Upfixe calcule votre co&ucirc;t par acquisition cible &agrave; partir de votre budget et taux de conversion.",
                },
                {
                  step: 2,
                  icon: <Filter className="h-6 w-6" />,
                  title: "Filtre d'accessibilit&eacute;",
                  desc: "Les mots-cl&eacute;s dont le CPC d&eacute;passe votre seuil de rentabilit&eacute; sont automatiquement &eacute;cart&eacute;s.",
                },
                {
                  step: 3,
                  icon: <Search className="h-6 w-6" />,
                  title: "Priorisation par volume",
                  desc: "Parmi les mots-cl&eacute;s accessibles, ceux avec le plus de volume de recherche passent en t&ecirc;te.",
                },
                {
                  step: 4,
                  icon: <Layers className="h-6 w-6" />,
                  title: "Regroupement th&eacute;matique",
                  desc: "Les mots-cl&eacute;s sont regroup&eacute;s par th&egrave;me pour maximiser la pertinence de chaque annonce.",
                },
                {
                  step: 5,
                  icon: <Zap className="h-6 w-6" />,
                  title: "Groupes d'annonces pr&ecirc;ts",
                  desc: "Des groupes d'annonces structur&eacute;s, pr&ecirc;ts &agrave; importer dans Google Ads en un clic.",
                },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp(i * 0.08)}
                  className="flex items-start gap-5 rounded-xl border border-border bg-card p-6"
                >
                  <div className="flex-shrink-0 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-lg">
                    {item.step}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-primary">{item.icon}</span>
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                    </div>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* ───────────────── Comparison ───────────────── */}
          <section className="mb-24">
            <motion.div {...fadeUp()} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Avant vs Avec Upfixe</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                La diff&eacute;rence est visible d&egrave;s le premier jour.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Sans Upfixe */}
              <motion.div
                {...fadeUp(0)}
                className="rounded-xl border border-destructive/20 bg-destructive/5 p-8"
              >
                <div className="flex items-center gap-2 mb-6">
                  <X className="h-6 w-6 text-destructive" />
                  <h3 className="text-xl font-bold text-destructive">Sans Upfixe</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "2 mots-cl&eacute;s actifs",
                    "42,50 &euro; CPC moyen",
                    "Budget &eacute;puis&eacute; &agrave; 10h15",
                    "1 groupe d'annonces g&eacute;n&eacute;rique",
                  ].map((line, i) => (
                    <li key={i} className="flex items-center gap-3 text-muted-foreground">
                      <X className="h-4 w-4 text-destructive flex-shrink-0" />
                      {line}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Avec Upfixe */}
              <motion.div
                {...fadeUp(0.1)}
                className="rounded-xl border border-primary/30 bg-primary/5 p-8"
              >
                <div className="flex items-center gap-2 mb-6">
                  <Check className="h-6 w-6 text-primary" />
                  <h3 className="text-xl font-bold text-primary">Avec Upfixe</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    "89+ mots-cl&eacute;s actifs",
                    "5,80 &euro; CPC moyen",
                    "Budget actif toute la journ&eacute;e",
                    "Groupes d'annonces th&eacute;matiques",
                  ].map((line, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                      {line}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </section>

          {/* ───────────────── Results / Stats ───────────────── */}
          <section className="mb-24">
            <motion.div {...fadeUp()} className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Des r&eacute;sultats mesurables</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Les chiffres parlent d'eux-m&ecirc;mes.
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {[
                { value: "+340 %", label: "Plus de mots-cl&eacute;s actifs", color: "text-primary" },
                { value: "-62 %", label: "Moins de d&eacute;penses gaspill&eacute;es", color: "text-green-600" },
                { value: "+85 %", label: "Meilleure pertinence", color: "text-primary" },
                { value: "3,2x", label: "Plus d'impressions", color: "text-green-600" },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  {...fadeUp(i * 0.1)}
                  className="rounded-xl border border-border bg-card p-6 text-center"
                >
                  <p className={`text-3xl md:text-4xl font-bold mb-2 ${stat.color}`}>{stat.value}</p>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* ───────────────── Final CTA ───────────────── */}
          <section>
            <motion.div
              {...fadeUp()}
              className="text-center bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-12 md:p-16 rounded-2xl"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Pr&ecirc;t &agrave; rentabiliser chaque euro ?
              </h2>
              <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                Rejoignez les annonceurs qui optimisent leur budget Google Ads au lieu de le gaspiller.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
                <Button
                  size="lg"
                  className="text-lg px-8 h-14 bg-white text-primary hover:bg-gray-100 font-semibold"
                  onClick={() => (window.location.href = getAuthUrl())}
                >
                  Essai gratuit <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 h-14 border-white/30 text-white hover:bg-white/10"
                  asChild
                >
                  <Link to="/pricing">Voir les tarifs</Link>
                </Button>
              </div>
              <p className="text-sm opacity-80">
                Aucune carte bancaire requise &bull; Configuration en 5 minutes &bull; Support en fran&ccedil;ais
              </p>
            </motion.div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default KeywordOptimisationFR;
